// Client for the metric registry API (services/dynamic-schema, mounted at
// /api/data/registry — see IMPORT_SPEC.md §2.2/§2.3, branch
// feat/metric-registry-api / PR #21).
//
// This is a NEW consolidated data-source client, not a guardrail file.
// data/metricRegistry.js and data/palettes.js stay exactly as they are and
// are consulted only as an OFFLINE FALLBACK by contexts/MetricRegistryContext
// — see that file's header for the fallback contract.
//
// GET /api/data/registry/metrics  -> { metrics: [...] }  (registry UNION
//   observed coverage — a metric present in `measurements` but never
//   registered still appears, with unregistered: true)
// GET /api/data/registry/aliases  -> { aliases: [...] }  (disease_key +
//   csv_aliases catalogue, scoped to is_active metrics)
//
// Both endpoints require auth (mounted under /api/data, which already
// requires a JWT for every request — see routes/data.js / index.js on the
// dynamic-schema service). dataAPI already attaches the bearer token via
// services/api.js's dataClient interceptor, same as every other /api/data/*
// call in this app.

import { dataAPI } from '../services/api'

// ---------------------------------------------------------------------------
// Normalization — camelCase, defaulted, one shape every accessor can rely on
// without null-checking each field individually.
// ---------------------------------------------------------------------------

export function normalizeMetric(row) {
  if (!row || !row.metric_type) return null
  return {
    metricType: row.metric_type,
    label: row.label ?? null,
    shortLabel: row.short_label ?? null,
    unit: row.unit ?? null,
    unitLabel: row.unit_label ?? null,
    category: row.category ?? null,
    aggregation: row.aggregation ?? null,
    palette: row.palette ?? null,
    decimals: Number.isFinite(row.decimals) ? row.decimals : null,
    valueMin: row.value_min ?? null,
    valueMax: row.value_max ?? null,
    diseaseKey: row.disease_key ?? null,
    csvAliases: Array.isArray(row.csv_aliases) ? row.csv_aliases : [],
    isActive: row.is_active ?? null,
    // Auto-discovery signal — true when this metric_type has data in
    // `measurements` but has never been registered. See file header.
    unregistered: !!row.unregistered,
    totalRecords: row.total_records ?? 0,
    sectorCount: row.sector_count ?? 0,
    locationCount: row.location_count ?? 0,
    earliest: row.earliest ?? null,
    latest: row.latest ?? null,
    lastImported: row.last_imported ?? null,
  }
}

export function normalizeAlias(row) {
  if (!row || !row.metric_type) return null
  return {
    metricType: row.metric_type,
    label: row.label ?? null,
    diseaseKey: row.disease_key ?? null,
    csvAliases: Array.isArray(row.csv_aliases) ? row.csv_aliases : [],
  }
}

// ---------------------------------------------------------------------------
// Fetchers — thin wrappers around dataAPI. Callers (MetricRegistryContext)
// decide what to do on rejection; this module never swallows an error.
// ---------------------------------------------------------------------------

export async function fetchMetricRegistry() {
  const res = await dataAPI.getRegistryMetrics()
  const rows = res?.metrics ?? res?.data?.metrics ?? []
  return rows.map(normalizeMetric).filter(Boolean)
}

export async function fetchMetricAliases() {
  const res = await dataAPI.getRegistryAliases()
  const rows = res?.aliases ?? res?.data?.aliases ?? []
  return rows.map(normalizeAlias).filter(Boolean)
}

// ---------------------------------------------------------------------------
// Category display — the one piece of presentation data
// data/metricRegistry.js never carried (it only ever had label + the
// count-like/aggregation set, see its own header comment). Previously each
// caller either hardcoded its own label→category map (DataBrowserPage.jsx)
// or its own enum→display-string map (utils/csvPreviewParser.js's
// CATEGORY_LABELS). This is the one copy now; csvPreviewParser.js keeps its
// own copy deliberately (see that file — it must keep working fully
// offline for other consumers this track doesn't touch).
// ---------------------------------------------------------------------------

const CATEGORY_DISPLAY = {
  disease: 'Health / Disease',
  meteorological: 'Meteorological',
  air_pollutant: 'Air Pollutant',
  vegetation_water: 'Vegetation & Water',
}

export function categoryDisplayLabel(category) {
  return CATEGORY_DISPLAY[category] || 'Other'
}

// Offline fallback for `category` — used only when the registry API is
// unreachable or hasn't loaded yet. Copied from the map DataBrowserPage.jsx
// used to keep inline (removed there in favour of this single copy) so a
// total API outage reproduces exactly today's behaviour for these metrics,
// not a regression to "Other" for something that used to categorise
// correctly. Deliberately NOT part of data/metricRegistry.js (guardrail) —
// that file has never carried category data, so extending it would be an
// edit to a file this track must not touch.
const OFFLINE_CATEGORY_BY_METRIC = {
  malaria_incidence: 'disease',
  flu_incidence: 'disease',
  flu_incidence_revised: 'disease',
  pneumonia_incidence: 'disease',
  diarrhea_cases: 'disease',
  temperature_max: 'meteorological',
  temperature_min: 'meteorological',
  rainfall: 'meteorological',
  relative_humidity: 'meteorological',
  specific_humidity: 'meteorological',
  pm25: 'air_pollutant',
  carbon_monoxide: 'air_pollutant',
  nitrogen_dioxide: 'air_pollutant',
  ozone: 'air_pollutant',
  sulfur_dioxide: 'air_pollutant',
  formaldehyde: 'air_pollutant',
  ndvi: 'vegetation_water',
  ndwi: 'vegetation_water',
  evi: 'vegetation_water',
}

export function offlineCategoryFor(metricType) {
  return OFFLINE_CATEGORY_BY_METRIC[metricType] || null
}

// ---------------------------------------------------------------------------
// CSV filename alias index — metric_type/unit/category keyed by every
// filename stem the registry knows for that metric (row.csvAliases). Built
// from the already-fetched /registry/metrics response rather than a second
// round trip to /registry/aliases: that endpoint only returns
// metric_type/label/disease_key/csv_aliases (no unit/category), which is
// not enough on its own for csvPreviewParser.js's filename -> {metricType,
// unit, category} detection. Every metrics row already carries its own
// csv_aliases alongside unit/category, so one fetch covers both needs.
// ---------------------------------------------------------------------------

export function buildCsvAliasIndex(metrics) {
  const index = {}
  for (const m of metrics || []) {
    for (const alias of m.csvAliases || []) {
      index[alias] = { metricType: m.metricType, unit: m.unit, category: m.category }
    }
  }
  return index
}
